import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { setSidebarOpen, toggleSidebarExpanded, setSidebarExpanded } from '../../store/slices/uiSlice';
import { logout } from '../../store/slices/authSlice';
import AntiguaFlag from '../UI/AntiguaFlag';

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { sidebarOpen, sidebarExpanded } = useAppSelector((state) => state.ui);
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile/desktop breakpoint (lg: 1024px)
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
    };

    // Initial check
    checkMobile();

    // Listen for resize events
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Reset drawer state when switching between mobile and desktop
  useEffect(() => {
    // If switching from mobile to desktop (or vice versa), close the mobile drawer
    if (!isMobile && sidebarOpen) {
      dispatch(setSidebarOpen(false));
    }
  }, [isMobile, sidebarOpen, dispatch]);

  // Show mini-drawer by default when authenticated
  useEffect(() => {
    if (isAuthenticated && !sidebarOpen) {
      // Mini-drawer is visible by default when authenticated
      // We don't need to set sidebarOpen, just ensure expanded state is false
      dispatch(setSidebarExpanded(false));
    }
  }, [isAuthenticated, dispatch, sidebarOpen]);

  const handleClose = () => {
    dispatch(setSidebarOpen(false));
  };

  const handleToggleExpand = () => {
    dispatch(toggleSidebarExpanded());
  };

  const handleLogout = () => {
    dispatch(logout());
    dispatch(setSidebarOpen(false));
    dispatch(setSidebarExpanded(false));
    navigate('/');
  };

  const navItems = [
    { path: '/', label: 'Home', icon: '🏠' },
    { path: '/projects', label: 'Community Projects', icon: '📋' },
    { path: '/vote', label: 'Vote', icon: '🗳️' },
    ...(isAuthenticated
      ? [
          { path: '/dashboard', label: 'My Impact', icon: '📊' },
          { path: '/profile', label: 'Profile', icon: '👤' },
          { path: '/projects/create', label: 'Create Project', icon: '➕' },
        ]
      : [
          { path: '/login', label: 'Sign In', icon: '🔐' },
          { path: '/register', label: 'Register', icon: '📝' },
        ]),
    { path: '/about', label: 'About', icon: 'ℹ️' },
  ];

  // Navigation items for mobile drawer top section (matches Header navigation)
  const headerNavItems = [
    { path: '/', label: 'Home' },
    { path: '/projects', label: 'Community Projects' },
    { path: '/vote', label: 'Vote' },
    ...(isAuthenticated
      ? [
          { path: '/dashboard', label: 'My Impact' },
          { path: '/projects/create', label: 'Create Project' },
        ]
      : []),
    { path: '/about', label: 'About' },
  ];

  // Show mobile drawer overlay when sidebarOpen is true
  if (sidebarOpen) {
    return (
      <>
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 lg:hidden"
          onClick={handleClose}
          aria-hidden="true"
        />

        {/* Mobile Drawer */}
        <aside
          className={`
            fixed top-0 left-0 h-full w-80 bg-[var(--color-default-background)] shadow-2xl z-50 transform transition-transform duration-300 ease-in-out lg:hidden
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
          role="navigation"
          aria-label="Main navigation"
          aria-hidden={!sidebarOpen}
        >
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[var(--color-neutral-border)] bg-[var(--color-neutral-50)]">
              <Link 
                to="/" 
                className="flex items-center gap-2 group" 
                onClick={handleClose}
              >
                <div className="w-8 h-6 flex-shrink-0">
                  <AntiguaFlag width={32} height={21} className="rounded-sm" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-[var(--color-default-font)] group-hover:text-[var(--color-brand-primary)] transition-colors">
                    ANTIGUA & BARBUDA
                  </h2>
                  <p className="text-xs text-[var(--color-subtext-color)]">
                    Citizens Portal
                  </p>
                </div>
              </Link>
              <button
                onClick={handleClose}
                className="p-2 text-[var(--color-neutral-600)] hover:text-[var(--color-default-font)] hover:bg-[var(--color-neutral-100)] rounded-lg transition-colors"
                aria-label="Close drawer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Top Navigation Section (from Header) */}
            <nav className="p-4 border-b border-[var(--color-neutral-border)] bg-[var(--color-neutral-50)]">
              <ul className="space-y-1">
                {headerNavItems.map((item) => {
                  const isActive = location.pathname === item.path || 
                    (item.path === '/dashboard' && location.pathname.startsWith('/dashboard')) ||
                    (item.path === '/projects' && location.pathname.startsWith('/projects') && !location.pathname.includes('/create'));
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={handleClose}
                        className={`
                          flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all text-sm
                          ${isActive
                            ? 'bg-[var(--color-brand-100)] text-[var(--color-brand-700)] font-medium shadow-sm'
                            : 'text-[var(--color-default-font)] hover:bg-[var(--color-neutral-100)]'}
                        `}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* User info section (if authenticated) */}
            {isAuthenticated && user && (
              <div className="p-4 border-b border-[var(--color-neutral-border)] bg-[var(--color-neutral-50)]">
                <Link
                  to="/profile"
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--color-neutral-100)] transition-colors group"
                  onClick={handleClose}
                >
                  {user.profile?.avatar ? (
                    <img src={user.profile.avatar} alt="Avatar" className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 bg-[var(--color-brand-primary)] rounded-full flex items-center justify-center text-white font-semibold text-base flex-shrink-0">
                      {user.profile?.displayName?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--color-default-font)] truncate group-hover:text-[var(--color-brand-primary)] transition-colors">
                      {user.profile?.nickname ? user.profile?.nickname : user.profile?.displayName || 'User'}
                    </p>
                    <p className="text-xs text-[var(--color-subtext-color)] truncate">
                      {user.email}
                    </p>
                  </div>
                </Link>
              </div>
            )}

            {/* Additional Navigation Items */}
            <nav className="flex-1 overflow-y-auto p-4">
              <ul className="space-y-1">
                {navItems.filter(item => !headerNavItems.find(hn => hn.path === item.path)).map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={handleClose}
                        className={`
                          flex items-center gap-3 px-4 py-3 rounded-lg transition-all
                          ${isActive
                            ? 'bg-[var(--color-brand-100)] text-[var(--color-brand-700)] font-medium shadow-sm'
                            : 'text-[var(--color-default-font)] hover:bg-[var(--color-neutral-100)]'}
                        `}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        <span className="text-lg">{item.icon}</span>
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Footer actions */}
            <div className="p-4 border-t border-[var(--color-neutral-border)] bg-[var(--color-neutral-50)]">
              {isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[var(--color-error-600)] hover:bg-[var(--color-error-50)] rounded-lg transition-colors font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Sign Out</span>
                </button>
              ) : (
                <div className="flex flex-col gap-2">
                  <Link
                    to="/login"
                    onClick={handleClose}
                    className="w-full text-center px-3 py-2.5 text-sm text-[var(--color-header-link-primary)] hover:bg-[var(--color-neutral-100)] rounded-lg transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    onClick={handleClose}
                    className="w-full text-center px-3 py-2.5 text-sm bg-[var(--color-header-link-active)] text-white rounded-lg hover:bg-[var(--color-brand-700)] transition-colors"
                  >
                    Register
                  </Link>
                </div>
              )}
            </div>
          </div>
        </aside>
      </>
    );
  }

  // For authenticated users on desktop: show as persistent mini-drawer
  // Hidden on mobile (lg:block)
  if (!isAuthenticated) {
    return null; // No sidebar for non-authenticated users on desktop
  }

  const isExpanded = sidebarExpanded;
  const sidebarWidth = isExpanded ? 'w-80' : 'w-20';

  return (
    <aside
      className={`
        hidden lg:block fixed top-[130px] left-0 h-[calc(100vh-110px)] ${sidebarWidth} bg-[var(--color-default-background)] border-r border-[var(--color-neutral-border)] z-30 transform transition-all duration-300 ease-in-out
        shadow-sm
      `}
      role="navigation"
      aria-label="Main navigation"
      onMouseEnter={() => !isExpanded && dispatch(setSidebarExpanded(true))}
      onMouseLeave={() => isExpanded && dispatch(setSidebarExpanded(false))}
    >
      <div className="flex flex-col h-full">
        {/* Header - Mini mode shows only flag, expanded shows full branding */}
        <div className="flex items-center justify-between p-3 border-b border-[var(--color-neutral-border)] bg-[var(--color-neutral-50)]">
          {isExpanded ? (
            <Link 
              to="/" 
              className="flex items-center gap-2 group flex-1"
            >
              <div className="w-8 h-6 flex-shrink-0">
                <AntiguaFlag width={32} height={21} className="rounded-sm" />
              </div>
              <div>
                <h2 className="text-xs font-bold text-[var(--color-default-font)] group-hover:text-[var(--color-brand-primary)] transition-colors">
                  ANTIGUA & BARBUDA
                </h2>
                <p className="text-[10px] text-[var(--color-subtext-color)]">
                  Citizens Portal
                </p>
              </div>
            </Link>
          ) : (
            <Link 
              to="/" 
              className="flex items-center justify-center w-full"
            >
              <div className="w-8 h-6">
                <AntiguaFlag width={32} height={21} className="rounded-sm" />
              </div>
            </Link>
          )}
          {isExpanded && (
            <button
              onClick={handleToggleExpand}
              className="p-1.5 text-[var(--color-neutral-600)] hover:text-[var(--color-default-font)] hover:bg-[var(--color-neutral-100)] rounded-lg transition-colors"
              aria-label="Collapse sidebar"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          )}
        </div>

        {/* User info section - Mini mode shows only avatar, expanded shows full info */}
        {user && (
          <div className="p-3 border-b border-[var(--color-neutral-border)] bg-[var(--color-neutral-50)]">
            {isExpanded ? (
              <Link
                to="/profile"
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--color-neutral-100)] transition-colors group"
              >
                {user.profile?.avatar ? (
                  <img src={user.profile.avatar} alt="Avatar" className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 bg-[var(--color-brand-primary)] rounded-full flex items-center justify-center text-white font-semibold text-base flex-shrink-0">
                    {user.profile?.displayName?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-[var(--color-default-font)] truncate group-hover:text-[var(--color-brand-primary)] transition-colors">
                    {user.profile?.nickname ? user.profile?.nickname : user.profile?.displayName || 'User'}
                  </p>
                  <p className="text-[10px] text-[var(--color-subtext-color)] truncate">
                    {user.email}
                  </p>
                </div>
              </Link>
            ) : (
              <Link
                to="/profile"
                className="flex items-center justify-center"
                title={user.profile?.displayName || user.email || 'Profile'}
              >
                {user.profile?.avatar ? (
                  <img src={user.profile.avatar} alt="Avatar" className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 bg-[var(--color-brand-primary)] rounded-full flex items-center justify-center text-white font-semibold text-base">
                    {user.profile?.displayName?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                  </div>
                )}
              </Link>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-2">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path === '/dashboard' && location.pathname.startsWith('/dashboard')) ||
                (item.path === '/projects' && location.pathname.startsWith('/projects') && !location.pathname.includes('/create'));
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all group
                      ${isActive
                        ? 'bg-[var(--color-brand-100)] text-[var(--color-brand-700)] font-medium shadow-sm'
                        : 'text-[var(--color-default-font)] hover:bg-[var(--color-neutral-100)]'}
                      ${!isExpanded ? 'justify-center' : ''}
                    `}
                    title={!isExpanded ? item.label : undefined}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <span className="text-lg flex-shrink-0">{item.icon}</span>
                    {isExpanded && <span className="text-sm whitespace-nowrap">{item.label}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer actions - Mini mode shows only icon, expanded shows full button */}
        <div className="p-3 border-t border-[var(--color-neutral-border)] bg-[var(--color-neutral-50)]">
          <button
            onClick={handleLogout}
            className={`
              w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[var(--color-error-600)] hover:bg-[var(--color-error-50)] rounded-lg transition-colors font-medium
              ${!isExpanded ? 'justify-center' : ''}
            `}
            title={!isExpanded ? 'Sign Out' : undefined}
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            {isExpanded && <span>Sign Out</span>}
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
